import {useEffect, useState} from "react";
import {
    OutputFormat,
    RcpRecipe,
    RcpStaticRecipe,
    RcpStatus,
    RecipeIngredient,
    RecipeIngredientSet,
    RecipeLevel,
    SourceImage
} from "./domain/RcpRecipe";
import {SelectEnum, TextEditor, Tooltip} from "./WebUtils";
import {FoodEntry, Nutrient} from "./domain/FoodEntry";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faAdd, faEdit, faSave, faTrash} from "@fortawesome/free-solid-svg-icons";
import {getCsrfToken} from "./App";
import {calculateNutrient, FoodSearchPopup, getFoodCacheEntry, readAllFoodEntries} from "./edit/FoodSearch";

let nextInternalId = 1;

function withInternalId(srcData: any[]) {
    srcData.forEach(si => si.internalId = nextInternalId++);
    return srcData;
}

/**
 * puts an internal ID on all ingredient lines within nested lists
 * @param rcp {[RecipeIngredientSet]}
 */
function rcpWithInternalId(rcp: any[]) {
    rcp.forEach(is => {
        is.ingredients.forEach((ing: any) => ing.internalId = nextInternalId++)
        is.internalId = nextInternalId++;
    });
    return rcp;
}

export function WebRecipeEntry() {
    const [rcpRecipe, setRcpRecipe] = useState<RcpStaticRecipe>(RcpStaticRecipe.EMPTY_STATIC_RECIPE);
    const [recipe, setRecipe] = useState<RcpRecipe | null>(RcpRecipe.EMPTY_RECIPE);
    const [ingredientSets, setIngredientSets] = useState<RecipeIngredientSet[]>([]);
    const [sourceImages, setSourceImages] = useState<SourceImage[]>([]);
    const [inlineError, setInlineError] = useState<string>("");
    const [readOnly, setReadOnly] = useState<boolean>(true);
    const [name, setName] = useState<string>("New");
    const [level, setLevel] = useState<string>("BEGINNER");
    const [servings, setServings] = useState<number>(4);
    const [handsOnTime, setHandsOnTime] = useState<number>(30);
    const [totalTime, setTotalTime] = useState<number>(60);
    const [foodCacheVersion, setFoodCacheVersion] = useState<number>(0);

    function editPressed() {
        if(!readOnly) {
            if(!recipe) return;
            let toSave = new RcpRecipe(recipe.id, name, RecipeLevel.fromString(level), recipe.servings,
                recipe.totalTime, recipe.handsOnTime, new Date(), ingredientSets, sourceImages);
            getCsrfToken().then((token: string) => {
                fetch(`/api/v1/nutriRcp/saveStatic`, {
                    method: "PUT",
                    headers: {'Content-Type': 'application/json',"X-XSRF-TOKEN": token},
                    body: JSON.stringify(toSave.toJson())
                }).then((response) => {
                    if (!response.ok) {
                        throw new Error(`HTTP error! Status: ${response.status}`);
                    }
                    return response.text();
                }).then((text) => {
                    setInlineError(text);
                });
            });
            setReadOnly(true);
        } else {
            if (rcpRecipe.status !== RcpStatus.OK || recipe == null) {
                setInlineError("Can only edit recipe in status OK with a valid recipe");
                return;
            }
        }
        setReadOnly(!readOnly);
    }

    function generateRecipe() {
        if(rcpRecipe.status !== RcpStatus.OK || recipe == null) {
            setInlineError("Can only build recipe in status OK with a valid recipe");
            return;
        }
        getCsrfToken().then((token: string) => {
            fetch(`/api/v1/nutriRcp/buildRcp/${rcpRecipe.uuid}`, {
                method: "POST", headers: {"Content-Type": "application/json", "Accept": "application/json", "X-XSRF-TOKEN": token},
            }).then((response) => {
                if (!response.ok) {
                    setInlineError("Build has failed");
                } else {
                    setInlineError("Build has succeeded");
                }
            })
        });
    }

    function createNewIngredientSet() {
        if(readOnly || rcpRecipe.status !== RcpStatus.OK || recipe == null) {
            setInlineError("Can only create new ingredient set in status OK with a valid recipe");
            return;
        }
        setIngredientSets([...ingredientSets, new RecipeIngredientSet("New", [], nextInternalId++)]);
    }

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const recipeUuid = urlParams.get('id') || "";
        getCsrfToken().then((token: string) => {
            fetch(`/api/v1/nutriRcp/getByUuid/static/${recipeUuid}`, {
                method: "POST",
                headers: {"Content-Type": "application/json", "Accept": "application/json", "X-XSRF-TOKEN": token},
            }).then((response) => {
                if (!response.ok) {
                    throw new Error(`HTTP error! Status: ${response.status}`);
                }
                return response.json(); // Parse JSON response
            }).then((jsonData: any) => {
                const r = RcpStaticRecipe.fromJson(jsonData);
                setRcpRecipe(r);
                setRecipe(r.rcp);
                setIngredientSets(rcpWithInternalId(r.rcp?.ingredientSets ?? []));
                setSourceImages((withInternalId(r.rcp?.sourceImages ?? [])));
                setName(r.rcp?.name ?? "New");
                setLevel(r.rcp?.level.name ?? "BEGINNER");
                setServings(r.rcp?.servings ?? 4);
                setTotalTime(r.rcp?.totalTime ?? 60);
                setHandsOnTime(r.rcp?.handsOnTime ?? 60);
                readAllFoodEntries(r.rcp, () => setFoodCacheVersion(v => v + 1)
            );
            })
        });
    }, []);

    function addNewSourceImage() {
        setSourceImages([...sourceImages, new SourceImage("New", "TBD", 400, 1024, OutputFormat.JPG_MED, nextInternalId++)]);
    }

    return (
        <div>
            <h1>View recipe {rcpRecipe.rcp?.name ?? "empty"}</h1>
            { inlineError ? <p style={{color: "red"}}>{inlineError}</p> : null }
            <button onClick={e => editPressed()}>{readOnly ? "Edit" : "Save"} recipe</button>
            &nbsp;<button onClick={e => generateRecipe()}>Generate recipe</button>&nbsp;
            { rcpRecipe.status === RcpStatus.MARKDOWN_MISSING_INGREDIENTS ? <pre>
                Please add the ingredients block to the markdown file as follows then refresh:<br/>
                    &#123;&#123;&lt; allIngredientStart &gt;&#125;&#125;<br/>
                    &#123;&#123;&lt; allIngredientEnd &gt;&#125;&#125;<br/>
                </pre> : <span/> }
            <h2>Static Recipe Information</h2>
            <table>
                <thead>
                <tr>
                    <th>Item</th>
                    <th>Value</th>
                </tr>
                </thead>
                <tbody>
                <tr>
                    <td>UUID</td>
                    <td>{rcpRecipe.uuid}</td>
                </tr>
                <tr>
                    <td>Status</td>
                    <td>{rcpRecipe.status.name}</td>
                </tr>
                <tr>
                    <td>JSON Path</td>
                    <td>{rcpRecipe.jsonPath}</td>
                </tr>
                <tr>
                    <td>Markdown Path</td>
                    <td>{rcpRecipe.markdownPath}</td>
                </tr>
                <tr>
                    <td>Last Updated</td>
                    <td>{recipe?.lastUpdated?.toLocaleString() ?? "Not known"}</td>
                </tr>
                <tr>
                    <td>Name</td>
                    <td><TextEditor size={60} readOnly={readOnly} value={name} setter={e => setName(e.target.value)} /></td>
                </tr>
                <tr>
                    <td>Level</td>
                    <td>{readOnly ? level : <SelectEnum value={level} onChange={e => setLevel(e)} values={RecipeLevel.ENUM_VALUES} />}</td>
                </tr>
                <tr>
                    <td>Servings</td>
                    <td><TextEditor readOnly={readOnly} value={servings} setter={e => setServings(parseInt(e.target.value))} /></td>
                </tr>
                <tr>
                    <td>Total Time (Minutes)</td>
                    <td><TextEditor readOnly={readOnly} value={totalTime} setter={e => setTotalTime(parseInt(e.target.value))} /></td>
                </tr>
                <tr>
                    <td>Hands On (Minutes)</td>
                    <td><TextEditor readOnly={readOnly} value={handsOnTime} setter={e => setHandsOnTime(parseInt(e.target.value))} /></td>
                </tr>
                </tbody>
            </table>
            <h2>Ingredient Sets</h2>
            {ingredientSets.map(is => <IngredientSetEntry readOnly={readOnly} ingredientSet={is} onChange={e => setIngredientSets(ingredientSets.map(i => i.internalId === is.internalId ? e : i))}/>)}
            {readOnly ? <span/> : <button onClick={e => createNewIngredientSet()}>Add Another Ingredient Set</button> }
            <h2>Source Images</h2>
            <table>
                <thead>
                    <tr>
                        <th>Fn</th>
                        <th>Directory<Tooltip text="Directory relative to the hugo 'data' directory"/></th>
                        <th>SrcImg<Tooltip text="The name of the image in the directory (forms output name too)"/></th>
                        <th>Reg Width</th>
                        <th>Max Width</th>
                        <th>Format<Tooltip text="The output format either PNG, or 40% or 50% JPEG" /></th>
                    </tr>
                </thead>
                <tbody>
                    {sourceImages.map(si =>
                        <SourceImageEntry readOnly={readOnly} sourceImage={si}
                                          onDeleteRecord={e => setSourceImages(e => e.filter(s => s.internalId !== si.internalId))}
                                          onChange={e => setSourceImages(sourceImages.map(s => s.internalId === si.internalId ? e : s))
                        }/>
                    )}
                    {readOnly ? "" : <tr><td colSpan={6}><button onClick={e => addNewSourceImage()}><FontAwesomeIcon icon={faAdd}/></button></td></tr> }
                </tbody>
            </table>
        </div>
    );
}

function IngredientSetEntry({ingredientSet, readOnly, onChange}: { ingredientSet: RecipeIngredientSet, readOnly: boolean, onChange: (is: RecipeIngredientSet) => void }) {
    const [name, setName] = useState<string>(ingredientSet.name ?? "New");
    const [ingredients, setIngredients] = useState<RecipeIngredient[]>(ingredientSet.ingredients);

    function addNewIngredient() {
        setIngredients([...ingredients, new RecipeIngredient(-1, -1, 1, "template", nextInternalId++)])
    }

    function ingredientsChanged(ing: RecipeIngredient) {
        const ingList = ingredients.map(i => i.internalId === ing.internalId ? ing : i);
        const newIng = new RecipeIngredientSet(name, ingList, ingredientSet.internalId);
        setIngredients(ingList);
        onChange(newIng);
    }

    function nameChanged(n: string) {
        setName(n);
        onChange(new RecipeIngredientSet(n, ingredients, ingredientSet.internalId));
    }

    function onDeleteIngredients(ing: RecipeIngredient) {
        setIngredients(ingredients.filter(i => i.internalId !== ing.internalId));
    }

    return <div className="ingredientSetEntry">
        <p>
            Name: <TextEditor size={30} readOnly={readOnly} value={name} setter={e => { nameChanged(e.target.value);}} />
        </p>
        <table>
        <thead>
        <tr>
            <th>Fn</th>
            <th>Ingredient</th>
            <th>Quantity</th>
            <th>Portion</th>
            <th>Template Text<Tooltip text="Supports variable expansion using ${...} for 'name', 'measure'(g), 'quanity'(10), 'amount'(10g), or any key from the pageMap file"/> </th>
            <th>Calories</th>
            <th>Fat</th>
            <th>Protein</th>
            <th>Fibre</th>
        </tr>
        </thead>
        <tbody>
        {ingredients.map(ing => <IngredientEditor readOnly={readOnly} value={ing} onChange={e => ingredientsChanged(e)}
                                                  deleteFoodEntry={() => onDeleteIngredients(ing)} key={ing.internalId}/>)}
        {readOnly ? <span/> : <tr><td colSpan={9}>
            <button onClick={e => addNewIngredient()}><FontAwesomeIcon icon={faAdd}/></button>
        </td></tr> }
        </tbody>
        </table>
    </div>
}

export function IngredientEditor({readOnly, value, deleteFoodEntry, onChange}: { readOnly: boolean, value: RecipeIngredient, deleteFoodEntry: (id: number) => void, onChange: (ing: RecipeIngredient) => void }) {
    const [quantity, setQuantity] = useState<number>(value.quantity);
    const [portionId, setPortionId] = useState<number>(value.portionSizeId);
    const [foodEntryId, setFoodEntryId] = useState<number>(value.foodEntryId);
    const [templateText, setTemplateText] = useState<string>(value.template);
    const [editing, setEditing] = useState<boolean>(false);

    /**
     * @param fe {FoodEntry}
     */
    function foodEntryChange(fe: FoodEntry) {
        setFoodEntryId(fe.id);
        setPortionId(-1);
        setQuantity(1);
        setEditing(true);
    }

    if(foodEntryId < 0) {
        return <tr><td colSpan={9}><FoodSearchPopup value={""} onFoodSelected={fe => foodEntryChange(fe)}/>
            <button onClick={e => deleteFoodEntry(value.internalId)}>Delete line</button>
        </td></tr>
    } else if(getFoodCacheEntry(foodEntryId) == null) {
        return <tr><td colSpan={9}>Loading food entry...</td> </tr>
    }
    const foodEntry = getFoodCacheEntry(foodEntryId);
    if (!foodEntry) return <tr><td colSpan={9}>Error loading food entry</td></tr>;

    const effectivePortion = quantity * (portionId > 0 ? (foodEntry as any)[portionId] ?? 1 : 1);


    function editingChanged() {
        if(editing) {
            onChange(new RecipeIngredient(foodEntryId, portionId, quantity, templateText, value.internalId));
        }
        setEditing(!editing);
    }

    return <tr>
        <td>
            {readOnly ? "" : <button onClick={e => editingChanged()}><FontAwesomeIcon icon={editing ? faSave : faEdit }/></button> }
            {readOnly ? "" : <button onClick={e => deleteFoodEntry(value.internalId)}><FontAwesomeIcon icon={faTrash}/></button>}
        </td>
        <td>
            {foodEntry.name} {!editing ? <span/> : <FoodSearchPopup onFoodSelected={fe => foodEntryChange(fe)} value={value} /> }
        </td>
        <td><TextEditor size={5} value={quantity} readOnly={!editing} setter={e => setQuantity(parseFloat(e.target.value))}/></td>
        <td><PortionSizeEditor portionId={portionId} portionSizes={foodEntry.portions} readOnly={!editing} portionChanged={e => setPortionId(parseInt(e))}/></td>
        <td><TextEditor size={40} value={templateText} readOnly={!editing} setter={e => setTemplateText(e.target.value)}/></td>
        <td>{calculateNutrient(Nutrient.CALORIES, effectivePortion, foodEntry.nutrients)}</td>
        <td>{calculateNutrient(Nutrient.TOTAL_FAT, effectivePortion, foodEntry.nutrients)}</td>
        <td>{calculateNutrient(Nutrient.PROTEIN, effectivePortion, foodEntry.nutrients)}</td>
        <td>{calculateNutrient(Nutrient.FIBRE, effectivePortion, foodEntry.nutrients)}</td>
    </tr>
}

function PortionSizeEditor({readOnly, portionId, portionChanged, portionSizes}: { readOnly: boolean, portionId: number, portionChanged: (val: string) => void, portionSizes: any[] }) {
    let ps = portionSizes.find(ps => ps.portionSizeId === portionId);

    if(readOnly) {
        return <span>{ps?.portionSizeName ?? "gram"}</span>;
    }

    return <select onChange={e => portionChanged(e.target.value)} value={portionId}>
        {portionSizes.map(ps => <option value={ps.portionSizeId}>{ps.portionSizeName}</option>)}
        <option value="-1">Gram</option>
    </select>

}

function SourceImageEntry({readOnly, sourceImage, onChange, onDeleteRecord}: { readOnly: boolean, sourceImage: SourceImage, onChange: (si: SourceImage) => void, onDeleteRecord: (id: number) => void }) {
    const [dir, setDir] = useState<string>(sourceImage.dir);
    const [imagePath, setImagePath] = useState<string>(sourceImage.srcImg);
    const [regularWidth, setRegularWidth] = useState<number>(sourceImage.regWidth);
    const [maxWidth, setMaxWidth] = useState<number>(sourceImage.maxWidth);
    const [format, setFormat] = useState<string>(sourceImage.outputFormat.name);
    const [editing, setEditing] = useState<boolean>(false);

    function onEditingChanged() {
        if(editing) {
            onChange(new SourceImage(dir, imagePath, regularWidth, maxWidth, OutputFormat.fromString(format), sourceImage.internalId));
        }
        setEditing(!editing);
    }

    return <tr>
        <td>
            {readOnly ? <span/> : <button onClick={e => onEditingChanged()}><FontAwesomeIcon icon={editing ? faSave : faEdit}/></button>}
            {readOnly ? <span/> : <button onClick={e => onDeleteRecord(sourceImage.internalId)}><FontAwesomeIcon icon={faTrash}/></button>}
        </td>
        <td>
            <TextEditor readOnly={!editing} value={dir} setter={e => setDir(e.target.value)} />
        </td>
        <td><TextEditor readOnly={!editing} value={imagePath} setter={e => setImagePath(e.target.value)} /></td>
        <td><TextEditor readOnly={!editing} value={regularWidth} setter={e => setRegularWidth(parseInt(e.target.value))} /> </td>
        <td><TextEditor readOnly={!editing} value={maxWidth} setter={e => setMaxWidth(parseInt(e.target.value))} /></td>
        <td>{!editing ? format : <SelectEnum value={format} values={OutputFormat.ENUM_VALUES} onChange={e => setFormat(e)} />}</td>
    </tr>
}